import { useState, useEffect } from 'react'
import AppSwitch from '../../../../../components/atoms/AppSwitch/AppSwitch'
import AppSelect from '../../../../../components/atoms/AppSelect/AppSelect'
import AppTooltip from '../../../../../components/atoms/AppTooltip/AppTooltip'
import { useProviders } from '../../../hooks/useProviders'
import { useProviderServices } from '../../../hooks/useProviderServices'

export interface ProviderObject {
  name: string
  code: string
}

export interface RouteChangeSelectorValue {
  provider: ProviderObject | null
  providerService: ProviderObject | null
}

interface RouteChangeSelectorProps {
  value: RouteChangeSelectorValue
  onChange: (value: RouteChangeSelectorValue) => void
}

export function RouteChangeSelector({ value, onChange }: RouteChangeSelectorProps) {
  const [isActive, setIsActive] = useState<boolean>(!!value.provider)
  
  useEffect(() => {
    if (value.provider) {
      setIsActive(true)
    }
  }, [value.provider])

  const { providers, loading: loadingProviders } = useProviders(isActive)
  
  const selectedProviderId = value.provider 
    ? providers.find(p => p.code === value.provider?.code)?.id ?? null 
    : null

  const { services, loading: loadingServices } = useProviderServices(isActive ? selectedProviderId : null)

  const handleToggle = (checked: boolean) => {
    setIsActive(checked)
    onChange({ provider: null, providerService: null })
  }

  const handleProviderChange = (code: string) => {
    const provider = providers.find(p => p.code === code)
    if (provider) {
      onChange({ provider: { name: provider.name, code: provider.code }, providerService: null })
    }
  }

  const handleServiceChange = (code: string) => {
    const service = services.find(s => s.code === code)
    if (service) {
      onChange({ ...value, providerService: { name: service.name, code: service.code } })
    }
  }

  const providerOptions = providers.map((p) => ({ value: p.code, label: `${p.code} - ${p.name}` }))
  const serviceOptions = services.map((s) => ({ value: s.code, label: `${s.code} - ${s.name}` }))

  return (
    <div className="w-full">
      {/* Toggle para Cambio de Ruta */}
      <div className="flex items-center gap-2 mb-3">
        <AppSwitch checked={isActive} onChange={handleToggle} />
        <span className="text-sm font-medium text-gray-500">Activar cambio de ruta</span>
        <AppTooltip
          text="Al activar, se reasigna el paquete a un nuevo proveedor y servicio de distribución según la configuración definida."
          position="right"
        />
      </div>

      {/* Proveedor y Servicio - solo si está activado */}
      {isActive && (
        <div className="flex gap-4 p-4 rounded-lg bg-[#F1F5F9] h-[98px]">
          <div className="flex-1">
            <AppSelect
              label="Proveedor"
              options={providerOptions}
              value={value.provider?.code ?? ''}
              onChange={(e) => handleProviderChange(e.target.value)}
              disabled={loadingProviders}
              placeholder={loadingProviders ? "Cargando proveedores..." : "Seleccionar proveedor"}
            />
          </div>
          <div className="flex-1">
            <AppSelect
              label="Servicio"
              options={serviceOptions}
              value={value.providerService?.code ?? ''}
              onChange={(e) => handleServiceChange(e.target.value)}
              disabled={selectedProviderId === null || loadingServices}
              placeholder={loadingServices ? "Cargando servicios..." : "Seleccionar servicio"}
            />
          </div>
        </div>
      )}
    </div>
  )
}
